import {
  ORDER_BRAND_OPTIONS,
  ORDER_STATUS_OPTIONS,
  ORDER_PERIOD_OPTIONS,
  DEFAULT_ORDER_PERIOD,
} from '../../lib/order-constants';
import type { OrderRecordTypeOption } from '../../lib/orders';

export interface OrdersFilterBarProps {
  brand: string;
  status: string;
  recordType: string;
  days: string;
  reminderActieVereist: boolean;
  telefoonOntbreekt: boolean;
  availableRecordTypes: OrderRecordTypeOption[];
  onChange: (patch: {
    brand?: string;
    status?: string;
    recordType?: string;
    days?: string;
    reminderActieVereist?: boolean;
    telefoonOntbreekt?: boolean;
  }) => void;
  onClear: () => void;
}

const SELECT_CLASS =
  'text-sm border border-slate-200 rounded-md px-2 py-1.5 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500';

export function OrdersFilterBar({
  brand,
  status,
  recordType,
  days,
  reminderActieVereist,
  telefoonOntbreekt,
  availableRecordTypes,
  onChange,
  onClear,
}: OrdersFilterBarProps) {
  const hasActiveFilters =
    Boolean(brand) ||
    Boolean(status) ||
    Boolean(recordType) ||
    days !== DEFAULT_ORDER_PERIOD ||
    reminderActieVereist ||
    telefoonOntbreekt;

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <select
        value={brand}
        onChange={(e) => onChange({ brand: e.target.value })}
        className={SELECT_CLASS}
        aria-label="Filter op merk"
      >
        {ORDER_BRAND_OPTIONS.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>

      <select
        value={recordType}
        onChange={(e) => onChange({ recordType: e.target.value })}
        className={SELECT_CLASS}
        aria-label="Filter op type"
      >
        <option value="">Alle types</option>
        {availableRecordTypes.map((rt) => (
          <option key={rt.developerName} value={rt.developerName}>
            {rt.name}
          </option>
        ))}
      </select>

      <select
        value={status}
        onChange={(e) => onChange({ status: e.target.value })}
        className={SELECT_CLASS}
        aria-label="Filter op status"
      >
        {ORDER_STATUS_OPTIONS.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>

      <select
        value={days}
        onChange={(e) => onChange({ days: e.target.value })}
        className={SELECT_CLASS}
        aria-label="Filter op periode"
      >
        {ORDER_PERIOD_OPTIONS.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>

      <label
        className="flex items-center gap-2 text-xs text-slate-700 px-2 py-1 border border-slate-200 rounded-md cursor-pointer hover:bg-slate-50"
        title="Toont alleen orders met openstaande reminder-actie (bounce of 3+ herinneringen)"
      >
        <input
          type="checkbox"
          checked={reminderActieVereist}
          onChange={(e) => onChange({ reminderActieVereist: e.target.checked })}
          className="rounded border-slate-300 text-red-600 focus:ring-red-500"
        />
        <span>Reminder-actie vereist</span>
      </label>

      <label
        className="flex items-center gap-2 text-xs text-slate-700 px-2 py-1 border border-slate-200 rounded-md cursor-pointer hover:bg-slate-50"
        title="Toont alleen opdrachten zonder telefoon-/mobielnummer op de klant — voor opvolging"
      >
        <input
          type="checkbox"
          checked={telefoonOntbreekt}
          onChange={(e) => onChange({ telefoonOntbreekt: e.target.checked })}
          className="rounded border-slate-300 text-amber-600 focus:ring-amber-500"
        />
        <span>Telefoon ontbreekt</span>
      </label>

      {hasActiveFilters && (
        <button
          type="button"
          onClick={onClear}
          className="text-xs text-indigo-600 hover:underline px-2 py-1"
        >
          Wis filters
        </button>
      )}
    </div>
  );
}
